import { useEffect, useRef } from 'react'

interface UseCanvasDeleteShortcutOptions {
  /** Nur im Maus-Werkzeug des sichtbaren Tabs (TabStack-Falle). */
  enabled: boolean
  /**
   * Das Canvas-Element, an dem gehorcht wird — NICHT das Dokument.
   * `null`, solange React Flow seine Pane noch nicht aufgebaut hat.
   */
  container: HTMLElement | null
  onDelete: () => void
}

/**
 * Entf und Backspace löschen die ausgewählten **Canvas-Elemente**.
 *
 * **Warum nicht React Flows `deleteKeyCode`:** Das horcht am Dokument und
 * fragt allein, ob der Tastendruck aus einem Eingabefeld kommt — nicht, wo der
 * Fokus liegt. Wer eine Notiz auswählte, dann in der **Aufgabenliste** eine
 * Aufgabe anklickte und Entf drückte, löschte BEIDES: Die Liste behandelte den
 * Tastendruck als ihren, und React Flow gleichzeitig als seinen.
 *
 * Am Canvas-Element gilt stattdessen die Regel, die man ohnehin erwartet und
 * die für ⌘D schon gilt (`useCanvasDuplicateShortcut`): **Es zählt, wo der
 * Fokus liegt.** Wer eine Notiz anklickt, fokussiert React Flows
 * Node-Hülle; wer einen Auswahlrahmen zieht, das Auswahlrechteck; wer den
 * gemeinsamen Rahmen einer Mehrfachauswahl bekommt, dessen Hülle
 * (`SelectionOverlay` holt sich den Fokus, sobald er erscheint). Alles davon
 * liegt im Canvas. Wer danach in die Aufgabenliste klickt, nimmt den Fokus
 * mit — dann löscht Entf wieder Aufgaben, und nur die.
 *
 * Aufgehalten wird nur, was wir auch verarbeiten: Bei jeder anderen Taste und
 * in jedem Eingabefeld läuft das Ereignis unangetastet weiter.
 *
 * @example
 * useCanvasDeleteShortcut({ enabled, container: domNode, onDelete })
 */
export function useCanvasDeleteShortcut({
  enabled,
  container,
  onDelete,
}: UseCanvasDeleteShortcutOptions) {
  // Über ein Ref, damit der Listener nicht bei jeder Änderung an der Auswahl
  // neu gebunden wird — während eines Ziehvorgangs wäre das jeder Frame.
  const onDeleteRef = useRef(onDelete)
  onDeleteRef.current = onDelete

  useEffect(() => {
    if (!enabled || !container) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      // Keine Zusatztaste: ⌥⌫ löscht ein Wort, ⌘⌫ eine Zeile — beides gehört
      // dem Text, nicht dem Canvas.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      /*
        Ein gehaltenes Entf soll NICHT weiterlaufen. Nach dem ersten Druck ist
        die Auswahl fort, jede Wiederholung liefe ins Leere — hielte aber das
        Ereignis auf und nähme es damit der Aufgabenliste weg.
      */
      if (e.repeat) return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return
      /*
        Im Text einer Notiz löscht Backspace Buchstaben, keine Memo.
        `contenteditable` zählt mit: Der Titel einer **Notiz** wird zwar in
        einem `<input>` geändert, aber die Regel soll nicht davon abhängen,
        welches Feld gerade welche Bauart hat.

        Über das Attribut und nicht über `isContentEditable`: jsdom rechnet
        die Eigenschaft nicht aus (nachgemessen — sie bleibt dort `false`,
        auch mit gesetztem Attribut), der Test liefe also ins Leere. `closest`
        fängt zugleich den Fall ab, dass der Fokus tiefer im bearbeitbaren
        Bereich steht.
      */
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (target?.closest('[contenteditable]:not([contenteditable="false"])')) return

      e.preventDefault()
      // Hält die Aufgabenliste ab, falls sie doch mithören sollte.
      e.stopPropagation()
      onDeleteRef.current()
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [enabled, container])
}
